import React, {useEffect, useRef, useState} from 'react';
import MonacoEditor from '@monaco-editor/react';
import {LockClosedIcon} from '@heroicons/react/outline';

import {useAnalytics, useAppContext} from '../../hooks';
import {Panel, PanelGroup, PanelResizer} from '../Panel';
import Button, {ButtonTypes} from '../ButtonNew';
import DropdownInput from '../DropdownInput';
import DismissibleCallout from '../DismissibleCallout';
import CodeEngine from './CodeEngine';
import SQLEngine from './SQLEngine';
import ConsolePanel from './Console';
import {toMonacoLanguage} from '../../util';

// TEMP: While we migrate to this editor, we'll disable
// it for lessons that already contain <SQLEditor>.
// Can remove `hasEditor` after updating content in CMS.
export const shouldShowEditor = (lesson) => {
  const isCodingQuestion = lesson?.lessonType === 'question' && !!lesson?.codeProblem;
  const hasEditor = lesson?.intro?.includes('SQLEditor');
  return isCodingQuestion && !hasEditor;
};

// Returns the correct 'engine' based on the lesson type
export const getEditor = (lesson) => {
  if (lesson?.codeProblem?.sqlCode) return SQLEngine;
  if (lesson?.codeProblem) return CodeEngine;
  return null;
};

// Sends the browser through www.tryexponent.com/migrate, which carries code
// drafts saved under the old domain over to this one, then comes back here.
const restoreLegacyDrafts = () => {
  localStorage.setItem('dismiss_callout: restore-tryexponent-drafts', 'true'); // Hack to force close DismissibleCallout

  const {pathname, search} = window.location;
  window.location.assign(`https://www.tryexponent.com/migrate?return=${encodeURIComponent(pathname + search)}`);
};

const signedUpBeforeDomainChange = (user) => new Date(user?.created_at) < new Date('2026-10-02');

export default function EditorPanel({
  lesson,
  questionId,
  onEditCode,
  onRunCode,
  onRunTests,
  onPassTests,
  onShareCode,
  topRightControl,
}) {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const [hasEdited, setHasEdited] = useState(false);
  const consoleRef = useRef(null);

  // Handles collapsing/expanding console panel on user input
  const onToggleConsole = () => {
    if (consoleRef?.current?.isExpanded()) {
      consoleRef?.current?.collapse();
    } else {
      consoleRef?.current?.expand();
    }
  };

  const handleEditCode = ({language, ...rest}) => {
    if (!hasEdited) {
      onEditCode?.({language, ...rest});
      setHasEdited(true);
    }
  };

  const handleRunCode = ({language, ...rest}) => {
    consoleRef?.current?.expand();
    onRunCode?.({language, ...rest});
  };

  const handleRunTests = ({language, ...rest}) => {
    consoleRef?.current?.expand();
    onRunTests?.({language, ...rest});
  };

  const handlePassTests = ({language, ...rest}) => {
    onPassTests?.({language, ...rest});
  };

  const handleShareCode = (language, content) => {
    onShareCode?.({language, content});
  };

  // Ensures the console panel is initially collapsed on mount
  useEffect(() => {
    consoleRef?.current?.collapse();
  }, [consoleRef.current]);

  useEffect(() => {
    if (!lesson) return;
    tracker.codeEditorView({lesson: lesson.slug, difficulty: lesson.difficulty, type: 'panel'});
    setHasEdited(false);
  }, [lesson]);

  // Returns correct editor type based on lesson
  const Editor = getEditor(lesson);

  return Boolean(shouldShowEditor(lesson) && Editor) && (
    <Editor
      code={lesson.codeProblem}
      key={lesson.slug}
      onEditCode={handleEditCode}
      onRunCode={handleRunCode}
      onRunTests={handleRunTests}
      onPassTests={handlePassTests}
    >
      {({
        content,
        languageOptions,
        activeLanguage,
        isRunning,
        isTesting,
        hasTests,
        testResults,
        consoleResults,
        onChange,
        onSubmit,
        onTest,
        onChangeLanguage,
        onReset,
        dirty,
      }) => (
        <div className="flex flex-col bg-white h-full w-full">
          <div className="w-full h-14 px-6 flex items-center justify-between gap-2 border-b border-gray-200 flex-shrink-0">
            <DropdownInput
              options={languageOptions}
              value={activeLanguage}
              onChange={onChangeLanguage}
              showReset={false}
              highlightActive={false}
              disabled={languageOptions.length < 2}
              native
            />
            {topRightControl}
          </div>
          <PanelGroup id="editor-panel-group" direction="vertical">
            <Panel id="editor-editor-panel">
              <MonacoEditor
                language={toMonacoLanguage(activeLanguage)}
                value={content}
                onChange={onChange}
                options={{
                  minimap: {
                    enabled: false,
                  },
                  automaticLayout: true,
                  lineNumbers: 'off',
                  padding: {top: 8},
                  lineDecorationsWidth: 0,
                  scrollbar: {
                    vertical: 'hidden',
                  },
                  fontSize: '14px',
                  renderLineHighlight: 'none',
                }}
              />
            </Panel>
            <Panel
              id="editor-console-panel"
              collapsible
              ref={consoleRef}
            >
              <PanelResizer direction="vertical" />
              <ConsolePanel
                hasTests={hasTests}
                isRunning={isRunning}
                isTesting={isTesting}
                consoleResults={consoleResults}
                testResults={testResults}
              />
            </Panel>
          </PanelGroup>
          {testResults?.failed === 0 && testResults?.passed > 0 && questionId && (
            <div className="px-4 py-2">
              <div className="flex justify-between items-center bg-indigo-100 py-2 px-4 rounded-lg text-indigo font-semibold">
                Well done! Share your solution to get peer feedback
                <Button onClick={() => handleShareCode(activeLanguage, content)}>
                  Share
                </Button>
              </div>
            </div>
          )}

          {/* TODO: Temporary. Consider removing around 4/1/27 */}
          {signedUpBeforeDomainChange(currentUser) && (
            <DismissibleCallout
              id="restore-tryexponent-drafts"
              className="flex-shrink-0 py-3 pl-6 pr-10 text-sm border-x-0 border-b-0 md:rounded-none md:shadow-none"
            >
              {'Looking for an older draft from Exponent? '}
              <button
                type="button"
                onClick={() => {
                  tracker.codeEditorRestoreDrafts({lesson: lesson.slug});
                  restoreLegacyDrafts();
                }}
                className="text-indigo-600 hover:text-indigo-700 font-medium cursor-pointer"
              >
                Restore
              </button>
            </DismissibleCallout>
          )}

          <div className="w-full pt-3 pb-1 px-4 flex justify-between border-t border-gray-200 flex-wrap">
            <Button
              buttonType={ButtonTypes.TERTIARY}
              onClick={onToggleConsole}
              className="pl-0 pr-2 mb-2"
            >
              Console
            </Button>
            <div className="ml-auto flex items-center">
              {dirty && (
                <Button
                  buttonType={ButtonTypes.TERTIARY}
                  onClick={onReset}
                  className="mr-2 mb-2"
                >
                  Reset
                </Button>
              )}
              {currentUser ? (
                <>
                  <Button
                    onClick={onSubmit}
                    disabled={isRunning || isTesting}
                    loading={isRunning}
                    buttonType={hasTests ? ButtonTypes.SECONDARY : ButtonTypes.PRIMARY}
                    className="mr-2 mb-2"
                  >
                    Run code
                  </Button>
                  {hasTests && (
                    <Button
                      onClick={onTest}
                      disabled={isRunning || isTesting}
                      loading={isTesting}
                      className="mb-2"
                    >
                      Run tests
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  href={currentUser ? '/upgrade?src=lesson' : '/signup?src=lesson'}
                  buttonType={ButtonTypes.SECONDARY}
                  className="mb-2"
                >
                  <LockClosedIcon className="h-4 w-4 mr-1 -ml-1" />
                  Sign up to run code
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Editor>
  );
}
